import {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  can,
  canWith,
  type Permission,
  type ProjectRole,
  type ProjectTreeDto,
  realtimeUserName,
  type ChannelSummaryDto,
  type LiveCallDto,
} from '@fyr/shared';
import { api, ApiError, type Me, type ProjectSummary } from '../api';
import { ContextSwitcher } from '../components/ContextSwitcher';
import { ReleaseBadge } from '../components/ReleaseBadge';
import { useBrand } from '../theme';
import { ArchiveBanner, ArchiveDialog, NewProjectDialog, useMe } from '../App';
import { useChatRoom } from '../chatRealtime';
import { Modal } from '../components/Modal';
import { TopbarTools } from '../components/TopbarTools';
import { ProjectTree, type TreeCommands } from '../components/ProjectTree';
import { FolderIcon, LibraryIcon } from '../components/icons';
import { buildTree, isDescendant, pathTo, type TreeNode } from '../tree-model';
import { BackButton } from '../components/PageCrumbs';
import { GoIcon } from '../components/commandIcons';
import { usePaletteEntries, type PaletteEntry } from '../commands';
import { nativeBridge } from '../native';

interface Toast {
  id: number;
  text: string;
  tone: 'ok' | 'warn';
  /** e.g. "Undo" after a delete (M4). */
  action?: { label: string; run: () => void };
}

type Dialog =
  | { type: 'newFolder'; parentId: string | null }
  | { type: 'newSheet'; folderId: string | null; isPrivate: boolean }
  | { type: 'move'; node: TreeNode }
  | { type: 'publish'; kind: 'sheet' | 'doc'; id: string; name: string; folderId?: string | null }
  | { type: 'newProject' }
  | { type: 'newBoard' }
  | { type: 'archive'; mode: 'archive' | 'unarchive' | 'retention' };

interface ProjectCtx {
  projectId: string;
  project: ProjectSummary | undefined;
  tree: ProjectTreeDto;
  nodes: TreeNode[];
  me: Me;
  role: ProjectRole;
  /** §23 a project permission from the role or a capability (M12.2). */
  can: (permission: Permission) => boolean;
  toast: (text: string, tone?: Toast['tone'], action?: Toast['action']) => void;
  commands: TreeCommands;
  /** Publish a private sheet or document (§12 M2.2) into a folder the user picks. */
  publish: (kind: 'sheet' | 'doc', id: string, name: string) => void;
  refreshTree: () => Promise<void>;
}

// the tree's New sheet / New board dialogs: their own chunk, loaded when one is opened
const NewSheetDialog = lazy(() => import('./ProjectDialogs').then((m) => ({ default: m.NewSheetDialog })));
const NewBoardDialog = lazy(() => import('./ProjectDialogs').then((m) => ({ default: m.NewBoardDialog })));

const Ctx = createContext<ProjectCtx | null>(null);
export function useProject() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useProject outside ProjectLayout');
  return v;
}
/** The project the shell's tree shows (null outside the shell) — e.g. for the Browser's Assistant (5 Nov fix). */
export const useShellProjectId = () => useContext(Ctx)?.projectId ?? null;
/** A page shown inside the project shell (top bar, tree, breadcrumbs): it leaves those out of its own layout. */
export const useInProjectShell = () => useContext(Ctx) !== null;

/** The tree beside a My Workspace page can be folded away (the Browser does): null where it can't. */
export interface WorkspaceTree {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
}
export const WorkspaceTreeCtx = createContext<WorkspaceTree | null>(null);
export const useWorkspaceTree = () => useContext(WorkspaceTreeCtx);

/** Projects grouped by programme (programmes in name order, then those in none). */
export function programmeGroups(projects: ProjectSummary[], none: string) {
  const groups = new Map<string, { key: string; label: string; projects: ProjectSummary[] }>();
  for (const p of projects) {
    const key = p.programme?.id ?? '';
    const label = p.programme ? `${p.programme.ref ? `${p.programme.ref} · ` : ''}${p.programme.name}` : none;
    if (!groups.has(key)) groups.set(key, { key, label, projects: [] });
    groups.get(key)!.projects.push(p);
  }
  return [...groups.values()].sort((a, b) =>
    a.key === '' ? 1 : b.key === '' ? -1 : a.label.localeCompare(b.label),
  );
}

/** Map the current route to the tree node it represents (selection + breadcrumbs). */
function selectedKeyFor(pathname: string): string | null {
  const m = pathname.match(
    /\/p\/[^/]+\/(g|f|d|cf|plan|workspace|board|workflows|actions|admin|dashboard|report|settings|chat|call)(?=\/|$)(?:\/([^/]+))?/,
  );
  if (!m) return null;
  const [, kind, id] = m;
  if (kind === 'dashboard') return id === 'exec' ? 'execDashboard' : 'dashboard';
  if (kind === 'report') return 'report';
  if (kind === 'chat' || kind === 'call') return 'chat';
  if (kind === 'settings') return 'root'; // the project brand (§23 M12.7: from the project's ⋯ menu)
  if (kind === 'workspace') return 'workspace';
  if (kind === 'workflows') return 'workflows';
  if (kind === 'actions') return 'actions';
  if (kind === 'admin') return 'people';
  if (kind === 'board') return `board:${id}`;
  if (kind === 'cf') return `connected:${id}`;
  if (kind === 'f') return id === 'root' ? 'root' : `folder:${id}`;
  return `${{ g: 'sheet', d: 'doc', plan: 'plan' }[kind as 'g' | 'd' | 'plan']}:${id}`;
}

/**
 * A project's shell: the top bar, the tree, breadcrumbs, and the page (the route's, or `children` — a My Workspace
 * page shown beside this project's tree by WorkspaceShell, with `selected` its item in the tree).
 */
export function ProjectLayout({
  projectId: fixedProjectId,
  selected,
  treeCollapsed,
  children,
}: {
  projectId?: string;
  selected?: string;
  treeCollapsed?: boolean;
  children?: ReactNode;
} = {}) {
  const params = useParams() as { projectId?: string };
  const projectId = (fixedProjectId ?? params.projectId)!;
  const { t } = useTranslation();
  const nav = useNavigate();
  const loc = useLocation();
  const qc = useQueryClient();
  const me = useMe().data;
  const { brand, logoUrl } = useBrand(me?.tenant.id);
  const projects = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<ProjectSummary[]>('/projects'),
  });
  const treeQ = useQuery({
    queryKey: ['tree', projectId],
    queryFn: () => api.get<ProjectTreeDto>(`/projects/${projectId}/tree`),
  });
  // §19 M9.3 unread chat (this project's channels + my DMs), kept live through my inbox room.
  const chatQ = useQuery({
    queryKey: ['chat-list', projectId],
    queryFn: () => api.get<ChannelSummaryDto[]>(`/chat?projectId=${projectId}`),
    refetchInterval: 120_000,
  });
  const chatUnread = (chatQ.data ?? [])
    .filter((c) => c.projectId === projectId || c.projectId === null)
    .reduce((n, c) => n + c.unread, 0);
  useChatRoom(
    me ? realtimeUserName(me.tenant.id, me.id) : null,
    '',
    'chatRev',
    useCallback(() => {
      void qc.invalidateQueries({ queryKey: ['chat-list'] });
      void qc.invalidateQueries({ queryKey: ['calls-mine'] });
    }, [qc]),
  );
  // §19 M9.4 / M22 someone is calling me: a call that rang me — a channel's, DM's, group's or a meeting — that I've not
  // picked up, started in the last two minutes.
  const callsOn = me?.features.calls ?? true;
  const callsQ = useQuery({
    queryKey: ['calls-mine'],
    queryFn: () => api.get<LiveCallDto[]>('/calls/mine'),
    enabled: callsOn,
    refetchInterval: 60_000,
  });
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const ringing = (callsQ.data ?? []).find((c) => c.ringing && !dismissed.has(c.id));
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [drawer, setDrawer] = useState(false);
  const [width, setWidth] = useState(() => {
    try {
      return Number(localStorage.getItem('fyr.treeWidth')) || 280;
    } catch {
      return 280;
    }
  });

  const project = projects.data?.find((p) => p.id === projectId);
  // the personal Dashboard's Ask bar starts on the project last used (per organisation; a convenience only)
  const tenantId = me?.tenant.id;
  useEffect(() => {
    if (!tenantId || !project) return;
    try {
      localStorage.setItem(`fyr.lastProject.${tenantId}`, project.id);
    } catch {
      /* ignore */
    }
  }, [tenantId, project]);
  // data lifecycle: an archived project is in no list — the tree carries its name and archive state
  const projectName = project?.name ?? treeQ.data?.projectName ?? '…';
  const archive = treeQ.data?.archive ?? null;
  const toast = useCallback((text: string, tone: Toast['tone'] = 'ok', action?: Toast['action']) => {
    const id = Date.now() + Math.random();
    setToasts((ts) => [...ts, { id, text, tone, action }]);
    setTimeout(() => setToasts((ts) => ts.filter((x) => x.id !== id)), action ? 10000 : 6000);
  }, []);
  const refreshTree = useCallback(
    () => qc.invalidateQueries({ queryKey: ['tree', projectId] }),
    [qc, projectId],
  );

  const nodes = useMemo(
    () =>
      treeQ.data && me
        ? buildTree(treeQ.data, projectName, me.id, {
            workspace: t('tree.myWorkspace'),
            myDashboard: t('tree.myDashboard'),
            plans: t('tree.plans'),
            master: t('tree.masterPlan'),
            myPlan: t('tree.myPlan'),
            governance: t('gov.governance'),
            workflows: t('wf.title'),
            actions: t('kanban.actionsTitle'),
            reporting: can(treeQ.data.myRole as ProjectRole, 'report.read')
              ? {
                  section: t('reporting.section'),
                  // a flag (Platform → Customise): off hides it here, and the api refuses it
                  exec: me.features.exec_dashboard ? t('reporting.exec') : null,
                  dashboard: t('reporting.dashboard'),
                  report: t('reporting.report'),
                }
              : null,
            chat: me.features.chat ? { label: t('chat.title'), unread: chatUnread } : null,
            learning: me.features.learning ? t('learning.title') : null,
            mail: me.features.mail ? t('mail.title') : null,
            browser: me.features.browser && !nativeBridge() ? t('browser.title') : null,
            admin: me.isTenantAdmin
              ? { section: t('admin.section'), people: t('administration.title') }
              : null,
          })
        : [],
    [treeQ.data, me, projectName, t, chatUnread],
  );
  // Cmd/Ctrl+K: every place in this project's tree (the tree is already only what this person may see)
  const places = useMemo(() => {
    const out: PaletteEntry[] = [];
    const walk = (ns: TreeNode[], path: string[]) => {
      for (const n of ns) {
        if (n.href)
          out.push({
            id: `place:${n.key}`,
            label: n.label,
            context: path.join(' › ') || undefined,
            icon: <GoIcon />,
            run: () => nav(n.href!),
          });
        walk(n.children, [...path, n.label]);
      }
    };
    walk(nodes, []);
    return out;
  }, [nodes, nav]);
  usePaletteEntries('nav', places);
  const selectedKey = selected ?? selectedKeyFor(loc.pathname);
  const crumbs = (selectedKey && pathTo(nodes, selectedKey)) || [];

  const role = (treeQ.data?.myRole ?? 'reader') as ProjectRole;
  // data lifecycle: an archived project is read-only — no capability adds anything to it (the api agrees)
  const caps = archive ? [] : (me?.capabilities ?? []);
  const run = useCallback(
    async (p: Promise<unknown>, ok?: string) => {
      try {
        await p;
        await refreshTree();
        if (ok) toast(ok);
        return true;
      } catch (e) {
        toast(e instanceof ApiError ? e.message : String(e), 'warn');
        return false;
      }
    },
    [refreshTree, toast],
  );

  const commands: TreeCommands = {
    canManage: can(role, 'content.manage'),
    canPublish: can(role, 'grid.publish'),
    newFolder: (parentId) => setDialog({ type: 'newFolder', parentId }),
    newSheet: (folderId, isPrivate) => setDialog({ type: 'newSheet', folderId, isPrivate }),
    rename: async (n, name) => {
      await run(
        n.kind === 'folder'
          ? api.patch(`/folders/${n.id}`, { name })
          : n.kind === 'doc'
            ? api.patch(`/documents/${n.id}`, { title: name })
            : api.patch(`/grids/${n.id}`, { name }),
      );
      if (n.kind === 'doc') void qc.invalidateQueries({ queryKey: ['document', n.id] });
      if (n.kind === 'sheet') void qc.invalidateQueries({ queryKey: ['grid', n.id] });
    },
    move: (node) => setDialog({ type: 'move', node }),
    drop: (dragged, folderId) => {
      if ((dragged.kind === 'sheet' || dragged.kind === 'doc') && dragged.isPrivate)
        setDialog({ type: 'publish', kind: dragged.kind, id: dragged.id!, name: dragged.label, folderId });
      else void moveTo(dragged, folderId);
    },
    publish: (node, folderId) =>
      setDialog({
        type: 'publish',
        kind: node.kind === 'doc' ? 'doc' : 'sheet',
        id: node.id!,
        name: node.label,
        folderId,
      }),
    canManageBoards: canWith(role, 'governance.manageBoards', caps),
    newBoard: () => setDialog({ type: 'newBoard' }),
    canBrand: canWith(role, 'project.brand', caps),
    openBrand: () => nav(`/p/${projectId}/settings/brand`),
    archived: !!archive,
    canArchive: !!me?.isTenantAdmin && !treeQ.data?.guest,
    archive: (mode) => setDialog({ type: 'archive', mode }),
  };

  async function moveTo(node: TreeNode, folderId: string | null) {
    const ok = await run(
      node.kind === 'folder'
        ? api.patch(`/folders/${node.id}`, { parentId: folderId })
        : node.kind === 'doc'
          ? api.patch(`/documents/${node.id}`, { folderId })
          : api.patch(`/grids/${node.id}`, { folderId }),
      t('tree.moved', { name: node.label }),
    );
    if (ok && node.kind === 'sheet') void qc.invalidateQueries({ queryKey: ['grid', node.id] });
  }

  // not in my list: home — unless it's an archived project I can still open (read-only; it's in no active list)
  if (
    projects.isSuccess &&
    !project &&
    !treeQ.isFetching &&
    (treeQ.isError || (treeQ.isSuccess && !treeQ.data.archive))
  )
    return <Navigate to="/" replace />;
  if (!me || !treeQ.data) return <div className="center-msg">…</div>;

  const ctx: ProjectCtx = {
    projectId,
    project,
    tree: treeQ.data,
    nodes,
    me,
    role,
    can: (permission) => canWith(role, permission, caps),
    toast,
    commands,
    publish: (kind, id, name) => setDialog({ type: 'publish', kind, id, name }),
    refreshTree,
  };

  return (
    <Ctx.Provider value={ctx}>
      <div
        className={`shell${treeCollapsed ? ' tree-collapsed' : ''}`}
        style={{ ['--tree-w' as string]: `${width}px` }}
      >
        <header className="topbar">
          <button
            className="btn icon-btn menu-btn"
            aria-label={t('tree.toggle')}
            onClick={() => setDrawer((d) => !d)}
          >
            ☰
          </button>
          {/* §17 white-label: the active tenant's / project's logo; Fyr's mark only when there is none. */}
          <Link to="/" className="brand">
            {logoUrl ? (
              <img className="brand-logo" src={logoUrl} alt={brand?.effective.name ?? ''} />
            ) : (
              <>
                <img src="/favicon.svg" alt="" width={22} height={22} />
                Fyr
              </>
            )}
            <ReleaseBadge />
          </Link>
          <ContextSwitcher
            contexts={me.contexts}
            activeTenantId={me.tenant.id}
            activeName={brand?.effective.name}
          />
          <span className="topbar-sep">›</span>
          {/* #143 Organisation › Programme › Project */}
          <Link to="/portfolio" className="topbar-link">
            {t('programmes.portfolio')}
          </Link>
          <span className="topbar-sep">›</span>
          {project?.programme && (
            <>
              <Link to={`/programme/${project.programme.id}`} className="topbar-link">
                {project.programme.ref ?? project.programme.name}
              </Link>
              <span className="topbar-sep">›</span>
            </>
          )}
          <select
            className="project-switch"
            aria-label={t('nav.project')}
            value={projectId}
            onChange={(e) => nav(`/p/${e.target.value}`)}
          >
            {/* #153 projects under their programmes, so the hierarchy shows where you switch */}
            {programmeGroups(projects.data ?? [], t('programmes.noProgramme')).map((g) => (
              <optgroup key={g.key} label={g.label}>
                {g.projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </optgroup>
            ))}
            {archive && !project && (
              <option value={projectId}>{t('archive.switcherName', { name: projectName })}</option>
            )}
          </select>
          {project?.canCreateProjects && (
            <button className="btn" onClick={() => setDialog({ type: 'newProject' })}>
              + {t('nav.newProject')}
            </button>
          )}
          <div className="spacer" />
          <TopbarTools me={me} />
        </header>

        <aside
          className={`sidebar${drawer ? ' open' : ''}`}
          onClick={(e) => (e.target as HTMLElement).closest('.tree-label') && setDrawer(false)}
        >
          <ProjectTree
            nodes={nodes}
            selectedKey={selectedKey}
            storageKey={`fyr.tree.${projectId}`}
            commands={commands}
          />
          <ResizeHandle width={width} onChange={setWidth} />
        </aside>

        <section className="content">
          {treeQ.data?.guest && (
            <p className="guest-banner" role="status">
              {t('share.guestBanner')}
            </p>
          )}
          {archive && (
            <Suspense fallback={null}>
              <ArchiveBanner
                archive={archive}
                onRestore={
                  commands.canArchive ? () => setDialog({ type: 'archive', mode: 'unarchive' }) : undefined
                }
              />
            </Suspense>
          )}
          <nav className="breadcrumbs page-crumbs" aria-label={t('tree.breadcrumbs')}>
            {/* #153 the same way back as every other page (up a level when opened straight from a link) */}
            <BackButton
              fallback={
                [...crumbs]
                  .slice(0, -1)
                  .reverse()
                  .find((c) => c.href)?.href ?? '/portfolio'
              }
            />
            {crumbs.map((c, i) => (
              <span key={c.key}>
                {i > 0 && <span className="sep">›</span>}
                {c.href && i < crumbs.length - 1 ? (
                  <Link to={c.href}>{c.label}</Link>
                ) : (
                  <span className={i === crumbs.length - 1 ? 'current' : ''}>{c.label}</span>
                )}
              </span>
            ))}
          </nav>
          {ringing && (
            <div className="ringing" role="alert">
              <span aria-hidden>{ringing.kind === 'audio' ? '📞' : '🎥'}</span>
              <strong>
                {ringing.scope === 'channel'
                  ? t('call.ringingIn', { name: ringing.startedBy.name, channel: ringing.channelName })
                  : t('call.ringing', { name: ringing.startedBy.name })}
              </strong>
              {ringing.scope !== 'dm' && ringing.invitees.length > 1 && (
                <span className="muted small ringing-with">
                  {t('call.with', {
                    names: ringing.invitees
                      .filter((p) => p.id !== me?.id)
                      .map((p) => p.name)
                      .join(', '),
                  })}
                </span>
              )}
              <span className="spacer" />
              <button
                className="btn btn-primary btn-small"
                onClick={() => nav(`/p/${ringing.projectId ?? projectId}/call/${ringing.id}`)}
              >
                {t('call.answer')}
              </button>
              <button
                className="btn btn-small"
                onClick={() => setDismissed(new Set([...dismissed, ringing.id]))}
              >
                {t('call.decline')}
              </button>
            </div>
          )}
          <div className="content-body">
            {/* Pages are lazy chunks (§17 perf): the tree stays put while one loads. */}
            <Suspense fallback={<div className="page-loading" aria-busy="true" />}>
              {children ?? <Outlet />}
            </Suspense>
          </div>
        </section>

        {dialog && <Suspense fallback={null}>{renderDialog(dialog, () => setDialog(null))}</Suspense>}
        {/* §8 changes (e.g. "Saved v0.2") are announced politely as well as shown */}
        <div className="toasts" role="status" aria-live="polite">
          {toasts.map((x) => (
            <div key={x.id} className={`toast ${x.tone}`}>
              {x.text}
              {x.action && (
                <button
                  className="toast-action"
                  onClick={() => {
                    x.action!.run();
                    setToasts((ts) => ts.filter((y) => y.id !== x.id));
                  }}
                >
                  {x.action.label}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </Ctx.Provider>
  );

  // A plain render function (not a nested component) so dialogs keep their state across layout re-renders.
  function renderDialog(d: Dialog, close: () => void) {
    switch (d.type) {
      case 'newFolder':
        return (
          <NameDialog
            title={t('tree.newFolder')}
            initial={t('tree.newFolderName')}
            onCancel={close}
            onSubmit={async (name) => {
              if (await run(api.post(`/projects/${projectId}/folders`, { name, parentId: d.parentId })))
                close();
            }}
          />
        );
      case 'newSheet':
        return (
          <NewSheetDialog
            isPrivate={d.isPrivate}
            onCancel={close}
            onSubmit={async (name, template) => {
              let id = '';
              const ok = await run(
                api
                  .post<{ id: string }>(`/projects/${projectId}/sheets`, {
                    name,
                    template,
                    folderId: d.folderId,
                    private: d.isPrivate,
                  })
                  .then((r) => (id = r.id)),
              );
              if (ok) {
                close();
                nav(`/p/${projectId}/g/${id}`);
              }
            }}
          />
        );
      case 'move':
        return (
          <FolderPickerDialog
            nodes={nodes}
            title={t('tree.moveTitle', { name: d.node.label })}
            confirm={t('tree.moveHere')}
            exclude={d.node.kind === 'folder' ? d.node.key : null}
            onCancel={close}
            onPick={async (folderId) => {
              close();
              await moveTo(d.node, folderId);
            }}
          />
        );
      case 'publish':
        return (
          <FolderPickerDialog
            nodes={nodes}
            title={t('publish.title', { name: d.name })}
            intro={t('publish.intro')}
            confirm={t('publish.confirm')}
            initial={d.folderId}
            exclude={null}
            onCancel={close}
            onPick={async (folderId) => {
              if (
                await run(
                  api.post(`/${d.kind === 'doc' ? 'documents' : 'grids'}/${d.id}/publish`, { folderId }),
                  t('publish.done', { name: d.name }),
                )
              ) {
                close();
                void qc.invalidateQueries({ queryKey: [d.kind === 'doc' ? 'document' : 'grid', d.id] });
              }
            }}
          />
        );
      case 'newBoard':
        return (
          <NewBoardDialog
            canProgramme={!!project?.programme}
            onCancel={close}
            onSubmit={async (input) => {
              let id = '';
              const ok = await run(
                api.post<{ id: string }>(`/projects/${projectId}/boards`, input).then((r) => (id = r.id)),
              );
              if (ok) {
                close();
                nav(`/p/${projectId}/board/${id}`);
              }
            }}
          />
        );
      case 'newProject':
        return (
          <Suspense fallback={null}>
            <NewProjectDialog onClose={close} />
          </Suspense>
        );
      case 'archive':
        return (
          <Suspense fallback={null}>
            <ArchiveDialog
              base={`/projects/${projectId}`}
              projectName={projectName}
              mode={d.mode}
              archive={archive}
              onClose={close}
              onDone={(message) => {
                void refreshTree();
                for (const k of [['projects'], ['portfolio'], ['admin', 'projects'], ['chat-list']])
                  void qc.invalidateQueries({ queryKey: k });
                toast(message);
              }}
            />
          </Suspense>
        );
    }
  }
}

function FolderPickerDialog(props: {
  nodes: TreeNode[];
  title: string;
  intro?: string;
  confirm: string;
  initial?: string | null;
  exclude: string | null;
  onCancel: () => void;
  onPick: (folderId: string | null) => void;
}) {
  const { t } = useTranslation();
  const { nodes } = props;
  const [picked, setPicked] = useState<string | null>(props.initial ?? null);
  const root = nodes.find((n) => n.kind === 'root')!;
  const rows: { node: TreeNode; depth: number }[] = [];
  const walk = (n: TreeNode, depth: number) => {
    if (props.exclude && (n.key === props.exclude || isDescendant(nodes, props.exclude, n.key))) return;
    rows.push({ node: n, depth });
    n.children.filter((c) => c.kind === 'folder').forEach((c) => walk(c, depth + 1));
  };
  walk(root, 0);
  return (
    <Modal
      title={props.title}
      onClose={props.onCancel}
      footer={
        <>
          <button className="btn" onClick={props.onCancel}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" onClick={() => props.onPick(picked)}>
            {props.confirm}
          </button>
        </>
      }
    >
      {props.intro && <p className="muted">{props.intro}</p>}
      <ul className="folder-picker" role="listbox" aria-label={props.title}>
        {rows.map(({ node, depth }) => {
          const id = node.kind === 'folder' ? node.id : null;
          return (
            <li
              key={node.key}
              role="option"
              aria-selected={picked === id}
              tabIndex={0}
              className={picked === id ? 'picked' : ''}
              style={{ paddingLeft: 8 + depth * 16 }}
              onClick={() => setPicked(id)}
              onDoubleClick={() => props.onPick(id)}
              onKeyDown={(e) => e.key === 'Enter' && props.onPick(id)}
            >
              {node.kind === 'root' ? <LibraryIcon /> : <FolderIcon />} {node.label}
            </li>
          );
        })}
      </ul>
    </Modal>
  );
}

function NameDialog({
  title,
  initial,
  onSubmit,
  onCancel,
}: {
  title: string;
  initial: string;
  onSubmit: (v: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [v, setV] = useState(initial);
  return (
    <Modal
      title={title}
      onClose={onCancel}
      footer={
        <>
          <button className="btn" onClick={onCancel}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" disabled={!v.trim()} onClick={() => onSubmit(v.trim())}>
            {t('common.create')}
          </button>
        </>
      }
    >
      <input
        className="text-input"
        autoFocus
        value={v}
        onFocus={(e) => e.target.select()}
        onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && v.trim() && onSubmit(v.trim())}
      />
    </Modal>
  );
}

function ResizeHandle({ width, onChange }: { width: number; onChange: (w: number) => void }) {
  const start = useRef<{ x: number; w: number } | null>(null);
  return (
    <div
      className="resize-handle"
      role="separator"
      aria-orientation="vertical"
      onPointerDown={(e) => {
        start.current = { x: e.clientX, w: width };
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        if (!start.current) return;
        onChange(Math.max(200, Math.min(520, start.current.w + e.clientX - start.current.x)));
      }}
      onPointerUp={() => {
        start.current = null;
        try {
          localStorage.setItem('fyr.treeWidth', String(width));
        } catch {
          /* ignore */
        }
      }}
    />
  );
}
